<template>
  <div
    :class="[
      fillHeight ? 'h-full' : '',
      'px-4 sm:px-6 lg:px-8 relative table-panel overflow-x-scroll',
    ]"
  >
    <div :class="[fillHeight ? 'h-full' : '', 'flow-root']">
      <div :class="[fillHeight ? 'h-full' : '', '-mx-4 sm:-mx-6 lg:-mx-8']">
        <div
          :class="[
            fillHeight ? 'h-full' : '',
            'inline-block min-w-full pt-0 align-middle  flex flex-col',
          ]"
          :style="minTableHeight ? { minHeight: minTableHeight } : {}"
        >
          <div class="w-full">
            <table class="min-w-full">
              <colgroup>
                <col v-if="selectable" style="width: 25px" />
                <slot name="colgroup" :data="data"></slot>
              </colgroup>
              <thead :class="headerClass + 'rounded-lg'">
                <slot name="header" :data="data" :selected="selected">
                  <tr>
                    <th scope="col" v-if="selectable">
                      <span class="sr-only">Select Rows</span>
                      <input
                        v-if="!selectAllDisabled"
                        ref="select-checkbox-global"
                        type="checkbox"
                        @click="toggleSelectAll"
                        :indeterminate.prop="
                          selected.length != 0 && selected.length != data.length
                        "
                      />
                    </th>
                    <slot name="header-row" :data="data"></slot>
                  </tr>
                  <tr>
                    <slot name="header-row-2" :data="data"></slot>
                  </tr>
                </slot>

                <slot name="secondHeader">
                  <tr v-if="$slots.secondHeaderRow">
                    <th v-if="selectable"></th>
                    <slot name="secondHeaderRow"></slot>
                  </tr>
                </slot>
              </thead>
              <tbody :class="bodyClass">
                <slot name="beforeBody">
                  <tr v-if="$slots.beforeBodyRow">
                    <td v-if="selectable"></td>
                    <slot name="beforeBodyRow"></slot>
                  </tr>
                </slot>

                <slot name="body" :data="data" :selected="selected">
                  <tr
                    v-if="!isLoading"
                    v-for="(row, index) in data"
                    :class="[
                      selected.includes(row[selectBy]) ? 'row-selected' : '',
                      row[deletedAtField] ? deletedAtClass : '',
                      ...rowClasses(row),
                    ]"
                    :key="row.id || row.uniqueKey"
                  >
                    <td v-if="selectable">
                      <input
                        v-if="selectableRequirement(row)"
                        type="checkbox"
                        class="select-checkbox"
                        :id="`select-checkbox-${index}`"
                        :value="row[selectBy]"
                        v-model="selectedLocal"
                      />
                    </td>
                    <slot name="row" :row="row" :row-index="index"></slot>
                  </tr>
                </slot>

                <slot name="afterBody">
                  <tr v-if="$slots.afterBodyRow">
                    <td v-if="selectable"></td>
                    <slot name="afterBodyRow"></slot>
                  </tr>
                </slot>
              </tbody>
              <!-- <tfoot
              v-if="data.length == 0 && !isLoading"
              class="text-muted mt-2 text-center"
            >
              <tr>
                <td colspan="100%" class="font-italic py-4">{{ emptyText }}</td>
              </tr>
            </tfoot> -->
            </table>
          </div>
          <div
            v-if="data.length == 0 || isLoading"
            class="flex-grow flex justify-center items-center w-full text-zinc-400 dark:text-zinc-700 min-h-32 py-4"
          >
            <v-icon
              v-if="isLoading"
              class="ov-spin"
              name="ri-loader-5-line"
              height="1em"
              width="1em"
              scale="1.5"
            />
            <template v-else-if="data.length == 0">
              <slot name="empty">
                <span v-html="emptyText" class="px-4 text-center text-sm"></span>
              </slot>
            </template>
          </div>
        </div>
        <slot name="inside-footer"></slot>
      </div>
    </div>
    <slot name="after">
      <div
        class="w-100 py-8"
        v-if="bottomLoading"
        :class="[bottomLoading ? 'visibile' : 'invisible']"
      >
        <!-- <Icon
          name="loading"
          class="w-10 h-10 m-auto text-green-500 spin-animation block"
        /> -->
      </div>
    </slot>
  </div>
</template>

<script setup>
import { ref, watch } from "vue"
import { useRouter } from "vue-router"

const router = useRouter()

const props = defineProps({
  isLoading: {
    type: Boolean,
    default: false,
  },
  deletedAtField: {
    type: String,
    default: "deletedAt",
  },
  deletedAtClass: {
    type: String,
    default: "deletedAtRow text-danger",
  },
  fillHeight: {
    type: Boolean,
    default: true,
  },
  minTableHeight: {
    type: String,
    default: "",
  },
  data: {
    type: Array,
    required: true,
  },
  selectable: {
    type: Boolean,
    default: false,
  },
  selectAllDisabled: {
    type: Boolean,
    default: false,
  },
  bottomLoading: {
    type: Boolean,
    default: false,
  },
  showBottomMore: {
    type: Boolean,
    default: false,
  },
  loading: {
    type: Boolean,
    default: false,
  },
  selectBy: {
    type: String,
    default: "id",
  },
  headerClass: {
    type: [String, Array],
    default: () => [],
  },
  bodyClass: {
    type: [String, Array],
    default: () => [],
  },
  selected: {
    type: Array,
    default: () => [],
  },
  selectableRequirement: {
    type: Function,
    default: () => true,
  },
  rowClasses: {
    type: Function,
    default: () => [],
  },
  emptyText: {
    type: String,
    default: "No items found",
  },
  blockStyle: {
    type: String,
    default: "",
  },
})

const selectedLocal = ref([])

const emit = defineEmits(["update:selected"])

function toggleSelectAll(event) {
  const isChecked = event.target.checked
  if (isChecked) {
    selectedLocal.value = props.data.map(item => item[props.selectBy])
  } else {
    selectedLocal.value = []
  }
}

watch(selectedLocal, (newArray, oldArray) => {
  const newValues = newArray.filter(value => !oldArray.includes(value))
  const removedValues = oldArray.filter(value => !newArray.includes(value))
  emit("update:selected", newArray)
})
</script>

<style scoped style="postcss">
table > tbody > tr:nth-child(even) {
  @apply bg-zinc-200 dark:bg-zinc-800;
}
table > tbody > th:first-child {
  background-color: #0fac1c !important;
}

table > tbody > th:last-child {
  background-color: #4603d6;
}

table > thead > tr > :slotted(th),
table > thead > tr > th {
  @apply uppercase px-3 pb-4 pt-4 text-left text-xs font-normal text-base-content border-b border-base-300;
  letter-spacing: 0.04rem;
}

table > thead > tr > :slotted(th:first-child),
table > thead > tr > th:first-child {
  @apply px-4 sm:pl-4;
}

table > thead > tr > :slotted(th:last-child),
table > thead > tr > th:last-child {
  /* @apply pl-3 pr-4 sm:pr-6 lg:pr-8; */
}

table > tbody {
  @apply dark:bg-base-100;
}

table > tbody > tr > :slotted(td),
table > tbody > tr > td {
  @apply whitespace-nowrap px-4 py-4 text-sm text-left;
}

table > tbody > tr > :slotted(td:first-child),
table > tbody > tr > td:first-child {
  @apply pl-4 pr-3 sm:pl-4;
}

table > tbody > tr > :slotted(td:last-child),
table > tbody > tr > td:last-child {
  /* @apply pl-3 pr-4 sm:pr-6 lg:pr-8; */
}

table > tbody > tr:last-child > :slotted(td:last-child),
table > tbody > tr:last-child > td:last-child {
  @apply rounded-br-lg;
}

table > tbody > tr:last-child > :slotted(td:first-child),
table > tbody > tr:last-child > td:first-child {
  @apply rounded-bl-lg;
}

.deletedAtRow {
  opacity: 0.5;
}

.x-loading tr td {
  @apply opacity-25;
  -webkit-filter: blur(1px);
  filter: blur(1px);
}
.row-selected {
  @apply bg-blue-300 border-base-300;
  background: rgba(243, 244, 246, 0.6) !important;
}
</style>
